import type { ReactElement } from 'react'
import { AuditInfoSection, ButtonEdit, NotReady, Page, TButton, TDrawer } from '@components'
import { useRedriveWebhook, useWebhooks } from '@front/model'
import { useQueryDisclosure, useToastError } from '@hooks'
import { Group } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import type { WebhookId } from '@terros/common'
import { useRequiredParams } from '@util'
import { WebhookDisplay } from './WebhookDisplay'
import { EditWebhookDrawerContent } from './WebhookEdit'
import { WebhookHistory } from './WebhookHistory'
import { createFileRoute } from '@tanstack/react-router'

function WebhookViewPage(): ReactElement {
  const { webhookId } = useRequiredParams<{ webhookId: WebhookId }>()

  const [edit, { open, close }] = useQueryDisclosure('edit')
  const { data: webhooks, error, isLoading, refetch, isFetching } = useWebhooks()

  const { add: redrive, isAdding, error: redriveError } = useRedriveWebhook()
  useToastError(redriveError)

  const webhook = webhooks?.find((w) => w.webhookId === webhookId)

  if (!webhook || error || isLoading)
    return <NotReady data={webhook} error={error} id='Webhook' isLoading={isLoading} />

  const onRedrive = async (): Promise<void> => {
    const { message } = await redrive({ webhookId })
    notifications.show({ title: 'Success', message, color: 'green' })
  }

  return (
    <>
      <Page>
        <Page.Heading back isFetching={isFetching} refetch={refetch} title='Webhook'>
          <Group>
            <TButton icon='publish' loading={isAdding} variant='secondary' onClick={onRedrive}>
              Redrive
            </TButton>
            <ButtonEdit disabled={isAdding} onClick={open} />
          </Group>
        </Page.Heading>
        <Page.Content scrollArea>
          <WebhookDisplay webhook={webhook} />
          <WebhookHistory webhook={webhook} />
          <AuditInfoSection {...webhook} />
        </Page.Content>
      </Page>
      <TDrawer opened={edit} title='Edit Webhook' onClose={close}>
        <EditWebhookDrawerContent webhook={webhook} onClose={close} />
      </TDrawer>
    </>
  )
}

export const Route = createFileRoute('/integration/webhook_/$webhookId/')({
  component: WebhookViewPage,
})
